import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useTelephone } from './useTelephone';
import PlayerAvatar from '../shared/PlayerAvatar';
import { ChevronRight, RotateCcw, X, Sparkles } from 'lucide-react';

/**
 * Broken Telephone — reveal phase.
 * Slideshow of each chain, showing prompt → drawing → guess → drawing → ...
 * Host controls navigation; all players see the same view.
 */
export default function TelephoneReveal() {
  const { state } = useApp();
  const { game, user, room } = state;
  const { advanceRevealStep, advanceRevealChain, playAgain, endGame } = useTelephone();
  const [animating, setAnimating] = useState(false);

  const chainOrder = game.chainOrder || [];
  const chainId = game.revealChain;
  const chain = game.chains?.[chainId];
  const revealStep = game.revealStep ?? -1;
  const totalRounds = game.totalRounds || chainOrder.length;

  // Which chain number are we on?
  const chainIndex = chainOrder.indexOf(chainId);
  const isLastChain = chainIndex >= chainOrder.length - 1;
  const isLastStep = revealStep >= totalRounds - 1;
  const allDone = isLastChain && isLastStep;

  // Chain starter's name
  const starterName = room.members?.[chainId]?.nickname || 'Unknown';

  // Animate new steps appearing
  useEffect(() => {
    setAnimating(true);
    const timeout = setTimeout(() => setAnimating(false), 400);
    return () => clearTimeout(timeout);
  }, [revealStep, chainId]);

  // Host: next step or next chain
  const handleNext = useCallback(() => {
    if (!user.isHost) return;
    if (isLastStep) {
      if (!isLastChain) {
        advanceRevealChain();
      }
    } else {
      advanceRevealStep();
    }
  }, [user.isHost, isLastStep, isLastChain, advanceRevealStep, advanceRevealChain]);

  // Build the visible steps (everything up to and including revealStep)
  const visibleSteps = [];
  if (chain) {
    // Always show the original prompt first
    visibleSteps.push({
      type: 'prompt',
      text: chain.prompt,
      userId: chainId,
      index: -1,
    });

    // Then each revealed step
    for (let i = 0; i <= revealStep; i++) {
      const step = chain.steps?.[i];
      if (step) {
        visibleSteps.push({ ...step, index: i });
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-charcoal font-display flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber" />
              Chain {chainIndex + 1} of {chainOrder.length}
            </h2>
            <p className="text-xs text-slate-400 font-display mt-0.5">
              Started by {starterName}
            </p>
          </div>
          {/* Progress dots */}
          <div className="flex items-center gap-1">
            {chainOrder.map((cId, idx) => (
              <div
                key={cId}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx < chainIndex
                    ? 'bg-amber'
                    : idx === chainIndex
                      ? 'bg-amber scale-125'
                      : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Scrollable chain steps */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {visibleSteps.map((step, idx) => {
            const isNewest = idx === visibleSteps.length - 1;
            const memberData = room.members?.[step.userId];
            const nickname = memberData?.nickname || 'Unknown';

            return (
              <div
                key={`${step.type}-${step.index}`}
                className={`transition-all ${
                  isNewest && animating
                    ? 'animate-fade-in-up'
                    : 'opacity-100'
                }`}
              >
                {/* Step label */}
                <div className="flex items-center gap-2 mb-2">
                  {memberData && (
                    <PlayerAvatar nickname={nickname} colour={memberData.colour} size="sm" />
                  )}
                  <span className="text-xs text-slate-400 font-display">
                    {step.type === 'prompt' && `${nickname}'s prompt`}
                    {step.type === 'draw' && `${nickname} drew`}
                    {step.type === 'guess' && `${nickname} guessed`}
                  </span>
                </div>

                {/* Step content */}
                {(step.type === 'prompt' || step.type === 'guess') ? (
                  <div className={`rounded-xl px-4 py-3 border ${
                    step.type === 'prompt'
                      ? 'bg-amber/5 border-amber/20'
                      : 'bg-sky-50 border-sky-100'
                  }`}>
                    <p className="text-base font-semibold text-charcoal font-display">
                      &ldquo;{step.text}&rdquo;
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                    {step.imageData ? (
                      <img
                        src={step.imageData}
                        alt={`Drawing by ${nickname}`}
                        className="w-full bg-canvas"
                      />
                    ) : (
                      <div className="w-full aspect-video bg-slate-50 flex items-center justify-center">
                        <p className="text-sm text-slate-300 font-display">No drawing</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Connector line between steps */}
                {idx < visibleSteps.length - 1 && (
                  <div className="flex justify-center py-1">
                    <div className="w-px h-4 bg-slate-200" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-slate-100">
          {allDone ? (
            /* All chains revealed — host can play again or end */
            user.isHost ? (
              <div className="flex gap-3">
                <button
                  onClick={playAgain}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Play Again
                </button>
                <button
                  onClick={endGame}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-display text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  End Game
                </button>
              </div>
            ) : (
              <p className="text-sm text-slate-400 font-display text-center">
                All chains revealed! Waiting for host...
              </p>
            )
          ) : (
            /* More steps/chains to reveal */
            user.isHost ? (
              <button
                onClick={handleNext}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
              >
                {isLastStep ? 'Next Chain' : 'Next Step'}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <p className="text-sm text-slate-400 font-display text-center">
                Host is revealing the chain...
              </p>
            )
          )}
        </div>
      </div>
    </div>
  );
}
